import classNames from 'clsx';
import { useContext } from 'react';
import { Field, Form } from 'react-final-form';
import { api } from 'src/client/api';
import { stores } from 'src/client/stores';
import {
  ADD_DEVICE_BUTTON,
  ADD_DEVICE_CODE_INVALID_ERROR,
  ADD_DEVICE_HEADLINE,
  ADD_DEVICE_REGISTRATION_CODE_PLACEHOLDER,
  ADD_DEVICE_TITLE,
} from 'src/common/constants/localizations/devices';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import { isRequired } from '../../../utils/form/validation';
import PillButton from '../../shared/button/PillButton';
import FormInput from '../../shared/form/FormInput';
import css from '../account-settings.module.scss';

const fieldName = 'registrationCode';

interface AddDeviceFormProps {
  successCallback: () => void;
}

interface AddDeviceFormValues {
  registrationCode: string;
}

interface AddDeviceFormSubmitErrors {
  base: string;
  registrationCode: true;
}

export function AddDeviceForm({ successCallback }: AddDeviceFormProps) {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);

  async function onSubmit({
    registrationCode,
  }: AddDeviceFormValues): Promise<void | AddDeviceFormSubmitErrors> {
    try {
      const device = await api.user.addDevice(registrationCode);
      stores.userStore.getState().addDevice(device);
      successCallback?.();
    } catch {
      return {
        base: getLocalizedText(ADD_DEVICE_CODE_INVALID_ERROR),
        // Having a truthy value here for the field itself will cause react-final-form to have the field
        // be in an invalid/error state, causing an error outline to show on the screen.
        registrationCode: true,
      };
    }
  }

  return (
    <Form<AddDeviceFormValues> onSubmit={onSubmit}>
      {({
        handleSubmit,
        pristine,
        hasValidationErrors,
        hasSubmitErrors,
        submitSucceeded,
        submitting,
        submitErrors,
      }) => (
        <form name="addDevice" data-testid="addDeviceForm" onSubmit={handleSubmit}>
          <h2 data-testid="addDeviceTitle" className={css.contentTitle}>
            {getLocalizedText(ADD_DEVICE_TITLE)}
          </h2>
          <p data-testid="headline" className={classNames(css.contentBodyText, css.withMargin)}>
            {getLocalizedText(ADD_DEVICE_HEADLINE)}
          </p>
          {hasSubmitErrors && (
            <p data-testid="mainError" className={css.errorField}>
              {submitErrors?.base as string}
            </p>
          )}
          <div className={classNames('row', css.inputWrapper)}>
            <div className="col-xs-12">
              <Field
                component={FormInput}
                id={fieldName}
                dataTestId={fieldName}
                name={fieldName}
                placeholder={getLocalizedText(ADD_DEVICE_REGISTRATION_CODE_PLACEHOLDER)}
                validate={isRequired()}
                required
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <PillButton
                id="addDeviceButton"
                type="submit"
                label={getLocalizedText(ADD_DEVICE_BUTTON)}
                className={css.ctaButton}
                isDisabled={pristine || submitting || submitSucceeded || hasValidationErrors}
                data-testid="addDeviceButton"
              />
            </div>
          </div>
        </form>
      )}
    </Form>
  );
}
